'use client';

import { isFeatureEnabled } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { DW_REPORTS, type DwReport } from './reports/registry';
import { useCustomFunctionalityPermissions } from '@/hooks/use-custom-functionality-permissions';
import type { CustomFunctionality } from '@/lib/permissions/custom-functionalities';
import { useGetCurrentUser } from '@/providers/session-provider/use-current-user';
import { ORGANIZATION_USER_DETAIL_ACCESS } from '@/utils/organization-user/constants';

/** A report's own flag (if any) is required on top of DATAWAREHOUSE. */
export function isDwReportFlagEnabled(report: DwReport): boolean {
  return !report.flag || isFeatureEnabled(report.flag);
}

/**
 * The shipped datawarehouse reports whose per-report flag (if any) is on
 * for this customer, and the aggregate grants behind them.
 */
function flagEnabledReports(): DwReport[] {
  return DW_REPORTS.filter(isDwReportFlagEnabled);
}

/**
 * An external ("Extern" detail access) user: the backend rejects every
 * datawarehouse endpoint — aggregate and search — for those, whatever
 * their role grants.
 */
export function useIsDwExternalUser(): boolean {
  const profile = useGetCurrentUser();
  return (
    profile.data?.detailAccess === ORGANIZATION_USER_DETAIL_ACCESS.EXTERNAL
  );
}

/**
 * The customer is entitled via the `DATAWAREHOUSE` feature flag (fail-CLOSED
 * — the beta is rolled out per customer) and the user is not external.
 */
function useDwEntitled(): boolean {
  const isExternal = useIsDwExternalUser();
  return isFeatureEnabled(FEATURE_FLAGS.DATAWAREHOUSE) && !isExternal;
}

/**
 * Whether the role holds one SPECIFIC dw_* grant — the backend checks the
 * grant of the endpoint each request hits (dw_aggr_space for spaces,
 * dw_aggr_revenue_ts for the revenue time series, …), so every query gates
 * on its own. Missing profiles, failed profile requests, and absent grants
 * deny access.
 */
export function useDwGrant(grant: CustomFunctionality): boolean {
  const entitled = useDwEntitled();
  const permissions = useCustomFunctionalityPermissions();
  return entitled && permissions.has(grant);
}

/** Report navigation and route guards share the same collection grants.
 * Wait for the profile and require an explicit grant before mounting reports.
 */
export function useAvailableDwReports() {
  const entitled = useDwEntitled();
  const permissions = useCustomFunctionalityPermissions();
  if (!entitled) return [];
  return flagEnabledReports().filter((report) =>
    permissions.has(report.grant)
  );
}
